import moment from "moment";
import { BackendApi } from "../../../utils/BackendApi";
import { AxiosResponse } from "axios";
import {
  SearchRecordRequest,
  UpdateRecordRequest,
  CreateRecordRequest,
} from "../../../class/record.schema";
import {
  CalendarData,
  calendarDataSchema,
} from "../../../class/frontend.schema";
import { ApiRoute } from "../../../utils/ApiRoute";
//#region Record
const getRecordData = (date: Date, callbackFunc: (value: any) => void) => {
  if (!date) {
    return false;
  }

  const startDate = moment(date)
    .add(-1, "month")
    .hour(0)
    .minute(0)
    .second(0)
    .toDate();
  const endDate = moment(date)
    .add(1, "month")
    .hour(0)
    .minute(0)
    .second(0)
    .toDate();

  const param: SearchRecordRequest = {
    start_date: startDate,
    end_date: endDate,
  };
  const succFunc = (resp: AxiosResponse) => {
    const recordData = transformRecordData(resp.data);
    if (typeof callbackFunc === "function") {
      callbackFunc(recordData);
    }
  };
  return BackendApi.get(ApiRoute.records, param, succFunc);
};

const transformRecordData = (data: any[]) => {
  const result: any[] = [];
  if (!data || !Array.isArray(data)) {
    throw new Error(`Record data is invalid!`);
  }
  data.forEach((item) => {
    const alreadyExist =
      result.find((x) => x.id.toString() === item.id.toString()) != null;
    if (alreadyExist) {
      return;
    }

    item.title = item.name;
    item.start = moment(item.start_date).toISOString();
    item.end = moment(item.end_date).toISOString();
    item.calendarIds = [item.calendar_id];
    if (item.place_id && item.place_id.toString().length > 0) {
      item.placeId = item.place_id;
    }
    const parseResult = calendarDataSchema.safeParse(item);
    if (!parseResult.success) {
      return;
    }
    result.push({ ...parseResult.data });
  });
  return result;
};

const addRecord = (
  calendar_ids: number[],
  param: UpdateRecordRequest,
  succFunc: (res: AxiosResponse) => void,
  errFunc: (res: AxiosResponse | string) => void,
) => {
  const data: CreateRecordRequest = {
    name: param.name ?? "",
    start_date: moment(param.start_date).toISOString(),
    end_date: moment(param.end_date).toISOString(),
    description: param.description ?? "",
    calendar_ids: calendar_ids,
    // place_id: param.placeId,
    is_annual_leave: false,
  };
  if (param.place_id && param.place_id > 0) {
    data[`place_id`] = param.place_id;
  }

  return BackendApi.post(ApiRoute.records, data, succFunc, errFunc);
};

const editRecord = (
  id: number,
  param: UpdateRecordRequest,
  succFunc: (res: AxiosResponse) => void,
  errFunc: (res: AxiosResponse | string) => void,
) => {
  const url = `${ApiRoute.records}/${id}`;
  const data: UpdateRecordRequest = {
    name: param.name ?? "",
    start_date: moment(param.start_date).toISOString(),
    end_date: moment(param.end_date).toISOString(),
    description: param.description,
    calendar_ids: param.calendar_ids,
  };

  if (param.place_id && param.place_id > 0) {
    data[`place_id`] = param.place_id;
  }

  return BackendApi.put(url, data, succFunc, errFunc);
};

const deleteRecord = (
  id: number,
  succFunc: (res: AxiosResponse) => void,
  errFunc: (res: AxiosResponse | string) => void,
) => {
  if (!id || id === -1) {
    throw new Error(`Id is invalid!`);
  }
  const url = `${ApiRoute.records}/${id}`;
  return BackendApi.put(
    url,
    {
      is_deleted: true,
    },
    succFunc,
    errFunc,
  );
};

//#endregion

//#region  Holiday Api
const getHolidayData = (date: Date, callbackFunc: (val: any) => void) => {
  if (!date) {
    return null;
  }
  const startDate = moment(date).add(-1, "year").format("YYYY-MM-DD");
  const endDate = moment(date).add(1, "year").format("YYYY-MM-DD");
  const param = {
    start_date: startDate,
    end_date: endDate,
  };
  const succFunc = (resp: AxiosResponse) => {
    const holidayData = transformHolidayData(resp.data);
    if (typeof callbackFunc === "function") {
      callbackFunc(holidayData);
    }
  };
  return BackendApi.get(ApiRoute.holiday, param, succFunc);
};

const transformHolidayData = (data: any) => {
  const result: CalendarData[] = [];
  if (!data || !Array.isArray(data)) {
    throw new Error(`Holiday data is invalid!`);
  }
  data.forEach((item) => {
    const diff = moment(item.end_date).diff(moment(item.start_date), "days");
    if (diff >= 1) {
      //item.end_date = moment(item.start_date).add(23, "hours").add(59, "minutes").add(59, "seconds").format(`YYYY-MM-DD`);
      item.end_date = item.start_date;
    }
    item.title = item.name;
    item.title_en = item.name;
    item.title_zh = item.description_zh;
    item.title_cn = item.description_cn;
    item.start = moment(item.start_date).toISOString();
    item.end = moment(item.end_date).toISOString();
    item.tag = "holiday"; // Mark as holiday
    item.allDay = true; // Mark as all day event
    item.display = "background"; // Mark as background event

    const parseResult = calendarDataSchema.safeParse(item);

    if (!parseResult.success) {
      return;
    }
    result.push({ ...parseResult.data });
  });
  return result;
};
//#endregion

const deleteCalendar = (
  id: number,
  succFunc: (res: AxiosResponse) => void,
  errFunc: (res: AxiosResponse | string) => void,
) => {
  if (!id || id === -1) {
    throw new Error(`Id is invalid!`);
  }
  const url = `${ApiRoute.calendar}/${id}`;
  return BackendApi.put(
    url,
    {
      is_deleted: true,
    },
    succFunc,
    errFunc,
  );
};

const deletePlace = (
  id: number,
  succFunc: (res: AxiosResponse) => void,
  errFunc: (res: AxiosResponse | string) => void,
) => {
  if (!id || id === -1) {
    throw new Error(`Id is invalid!`);
  }
  const url = `${ApiRoute.place}/${id}`;
  return BackendApi.put(
    url,
    {
      is_deleted: true,
    },
    succFunc,
    errFunc,
  );
};

const getPlaceData = (
  callbackFunc: (val: any) => void,
  keyword?: string,
  page?: number,
  pageSize?: number,
) => {
  const succFunc = (resp: AxiosResponse) => {
    const data = resp.data;
    if (typeof callbackFunc === "function") {
      callbackFunc(data);
    }
  };
  const param: any = {};
  if (keyword && keyword.length > 0) {
    param[`keyword`] = keyword;
  }
  if (page && page > 0) {
    param[`page`] = page;
  }
  if (pageSize && pageSize > 0) {
    param[`pageSize`] = pageSize;
  }
  return BackendApi.get(ApiRoute.place, param, succFunc);
};

const getCalendarData = (
  callbackFunc: (val: any) => void,
  keyword?: string,
  page?: number,
  pageSize?: number,
) => {
  const succFunc = (resp: AxiosResponse) => {
    const data = resp.data;
    if (typeof callbackFunc === "function") {
      callbackFunc(data);
    }
  };
  const param: any = {};
  if (keyword && keyword.length > 0) {
    param[`keyword`] = keyword;
  }
  if (page && page > 0) {
    param[`page`] = page;
  }
  if (pageSize && pageSize > 0) {
    param[`pageSize`] = pageSize;
  }
  return BackendApi.get(ApiRoute.calendar, param, succFunc);
};

const CalendarBackend = {
  getRecordData,
  addRecord,
  editRecord,
  deleteRecord,
  getHolidayData,
  getPlaceData,
  transformRecordData,
  transformHolidayData,
  getCalendarData,
  deleteCalendar,
  deletePlace,
};

export default CalendarBackend;
